import { ChangeDetectionStrategy, Component, Input, NgModule, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { ContactModalComponent, ContactTypeEnum } from '@commons/contact-modal/contact-modal.component';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import {
  YouthInfoCenterContactModalComponent,
  YouthInfoCenterModalConfig,
} from '@domains/youth-info-center/contact-modal/contact-modal.component';
import { YouthInfoCenterCardAddressModule } from '@domains/youth-info-center/youth-info-center-card-address/youth-info-center-card-address.component';
import { YouthInfoCenterCardDetailsModule } from '@domains/youth-info-center/youth-info-center-card-details/youth-info-center-card-details.component';
import { YouthInfoCenterTooltipModule } from '@domains/youth-info-center/youth-info-center-tooltip/youth-info-center-tooltip.component';
import { environment } from '@environment';
import { YouthInfoCenterListItem } from '@models/youth-info-center/youth-info-center';
import { YouthInfoCenterTypeEnum } from '@models/youth-info-center/youth-info-center-type.enum';
import { SharedModule } from '@shared/shared.module';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-youth-info-center-card [youthInfoCenter]',
  templateUrl: './youth-info-center-card.component.html',
  styleUrls: ['./youth-info-center-card.component.scss', './youth-info-center-card-structure.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YouthInfoCenterCardComponent {
  @Input() youthInfoCenter!: YouthInfoCenterListItem;
  @Input() elmId?: string;

  YouthInfoCenterTypeEnum = YouthInfoCenterTypeEnum;
  panelOpenState$ = new BehaviorSubject<boolean>(false);

  youthInfoCenterTypeLabels: Record<YouthInfoCenterTypeEnum, string> = {
    [YouthInfoCenterTypeEnum.CIJ]: 'Structure information jeunesse',
    [YouthInfoCenterTypeEnum.LOCALMISSION]: 'Mission locale',
    [YouthInfoCenterTypeEnum.APE]: 'Agence Pôle Emploi',
  };

  youthInfoCenterLogo: Record<YouthInfoCenterTypeEnum, string> = {
    [YouthInfoCenterTypeEnum.LOCALMISSION]: `${environment.wizbiiImages}/accompagnement/logounml.jpg`,
    [YouthInfoCenterTypeEnum.CIJ]: `${environment.wizbiiImages}/accompagnement/info-jeunes.png`,
    [YouthInfoCenterTypeEnum.APE]: `${environment.wizbiiImages}/accompagnement/pole-emploi.png`,
  };

  youthInfoContactLabel: Record<YouthInfoCenterTypeEnum, string> = {
    [YouthInfoCenterTypeEnum.CIJ]: 'Contacter',
    [YouthInfoCenterTypeEnum.APE]: 'Contacter',
    [YouthInfoCenterTypeEnum.LOCALMISSION]: 'Être recontacté(e)',
  };

  private static Counter = 0;
  private readonly _id = `wzb-apprentices-list-card-${++YouthInfoCenterCardComponent.Counter}`;

  get elm_id(): string {
    return this.elmId ?? this._id;
  }

  @ViewChild(MatExpansionPanel) panel!: MatExpansionPanel;

  get youthInfoCenterTypeLabel(): string {
    return this.youthInfoCenterTypeLabels[this.youthInfoCenter.type];
  }

  get youthInfoCenterLabel(): string {
    return this.youthInfoCenter.type === YouthInfoCenterTypeEnum.APE
      ? this.youthInfoCenter.addresses[0].lignes[1]
      : this.youthInfoCenter.addresses[0].city;
  }

  get youthInfoCenterCity(): string {
    return this.youthInfoCenter.addresses[0].city;
  }

  get youthInfoCenterTitle(): string {
    return `${this.youthInfoCenterTypeLabel} - ${this.youthInfoCenter.addresses[0].city}`;
  }

  get youthInfoCenterMapLink(): string {
    const address = this.youthInfoCenter.addresses[0];
    return `https://fr.mappy.com/plan#/${address.lignes.join(' ')} ${address.city}`;
  }

  get youthInfoCenterStreet(): string {
    return this.youthInfoCenter.addresses[0].lignes[0];
  }

  get youthInfoCenterZipCode(): string {
    return this.youthInfoCenter.addresses[0].zipCode;
  }

  get linkAriaLabel(): string {
    return `Voir l'emplacement de l'organisme ${this.youthInfoCenterTitle} sur la carte`;
  }

  get isCejOperator(): boolean {
    return [YouthInfoCenterTypeEnum.APE, YouthInfoCenterTypeEnum.LOCALMISSION].includes(this.youthInfoCenter.type);
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  get isMobileAndTabletPortrait$(): Observable<boolean> {
    return this.breakpointsService.isTabletPortrait$;
  }

  get contactLabel(): string {
    return this.youthInfoContactLabel[this.youthInfoCenter.type];
  }

  get logoSrc(): string {
    return this.youthInfoCenterLogo[this.youthInfoCenter.type];
  }

  trackEvent = trackEventWithAtInternet;

  constructor(private readonly breakpointsService: BreakpointsService, private readonly dialog: MatDialog) {}

  toggleExpansionPanel(): void {
    this.trackEvent({ category: 'Accompagnement', action: 'Click Détail', type: 'action' });

    this.panelOpenState$.next(!this.panelOpenState$.value);
  }

  openModal(): void {
    this.trackEvent({ category: 'Accompagnement', action: 'Click Contacter', type: 'action' });

    this.isMobile$.pipe(take(1)).subscribe({
      next: (isMobile) => {
        if ([YouthInfoCenterTypeEnum.APE, YouthInfoCenterTypeEnum.CIJ].includes(this.youthInfoCenter.type)) {
          this.dialog.open(YouthInfoCenterContactModalComponent, {
            ...this.getModalConfig(isMobile),
            data: {
              isMobile,
              centerType: this.youthInfoCenter.type,
              centerName: this.youthInfoCenterTitle,
              centerMail: this.youthInfoCenter.email,
            },
          });
        } else {
          this.dialog.open(ContactModalComponent, {
            ...this.getModalConfig(isMobile),
            data: {
              contactType: ContactTypeEnum.localMission,
              isMobile,
              localMission: this.youthInfoCenter,
            },
          });
        }
      },
    });
  }

  private getModalConfig(isMobile: boolean): YouthInfoCenterModalConfig {
    return {
      panelClass: isMobile ? 'wizbii-mobile-panel--white' : 'wizbii-panel--white',
      backdropClass: 'wzb-backdrop',
      minWidth: isMobile ? '100vw' : '50vw',
      autoFocus: false,
      disableClose: isMobile,
      position: isMobile ? { bottom: '0' } : {},
    };
  }

  trackByLine(_: number, line: string): string {
    return line;
  }
}

@NgModule({
  imports: [
    SharedModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    YouthInfoCenterTooltipModule,
    YouthInfoCenterCardDetailsModule,
    YouthInfoCenterCardAddressModule,
  ],
  declarations: [YouthInfoCenterCardComponent],
  exports: [YouthInfoCenterCardComponent],
})
export class YouthInfoCenterCardModule {}
